import { FC, useCallback, useEffect } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Style } from '@capacitor/status-bar';
import * as Sentry from '@sentry/capacitor';
import { User } from 'firebase/auth';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';

import {
	personalDetailsManager,
	useBusinessLayer,
} from '@mopla/business-logic';
import { SuccessStep } from '@mopla/constants';
import {
	Footer,
	IFormInput,
	linkPendingCredential,
	MainWithFooterTemplate,
	useStatusBarStyleSync,
} from '@mopla/ui';
import moplaWelcomeLogo from '@mopla/ui/icons/moplaWelcomeLogo.svg?url';
import { ContentWrapper } from '@mopla/ui/layouts/WelcomeScreen';
import { usePersistentCountdownGuard } from '@mopla/utils';

import { pushAuthEvent } from '../../../../../app/userTracking/events';
import { LoginScreen } from '../../../../organisms/LoginScreen/LoginScreen';
import { VerificationStatus } from '../../constants';
import { useWelcomePageStoreContext } from '../../useWelcomePageStore';
import { TSetInterceptor } from '../../utils/useInterceptor';
import { MoplaLogoImgStyled } from '../styled';
import { usePasswordlessLoginContext } from '../WithPasswordless';

export interface IEmailPasswordSignInFlowProps {
	setBackInterceptor: TSetInterceptor;
	setCloseInterceptor: TSetInterceptor;
	onBottomSliderClose: VoidFunction;
}

export const EmailPasswordSignInFlow: FC<IEmailPasswordSignInFlowProps> = ({
	setBackInterceptor,
	setCloseInterceptor,
	onBottomSliderClose,
}) => {
	const { api } = useBusinessLayer();
	const auth = getAuth();
	const navigate = useNavigate();
	const location = useLocation();
	const {
		dispatch,
		isAuthError,
		loginTitleComp,
		successType,
		setSuccessType,
		verificationStatus,
	} = useWelcomePageStoreContext();
	const { onPasswordlessLogin } = usePasswordlessLoginContext();
	const { reinitCountdownGuard } = usePersistentCountdownGuard('emailSendTime');

	const shouldHideLogo =
		successType === SuccessStep.pwlessLoginEmailSent ||
		successType === SuccessStep.resendPwlessLoginEmail;

	const isVerificationSuccessNotSameDevice =
		verificationStatus === VerificationStatus.successNotSameDeviceApp ||
		verificationStatus === VerificationStatus.successNotSameDeviceWeb;
	const isVerificationFailure =
		verificationStatus === VerificationStatus.rejectNoUserData ||
		verificationStatus === VerificationStatus.rejectWithUserData;

	const { push, pop } = useStatusBarStyleSync();

	const handleBottomSliderBackClick = useCallback(() => {
		dispatch({ type: 'SET_AUTH_ERROR', payload: false });
		navigate(-1);
		setSuccessType(null);

		return false;
	}, [dispatch, navigate, setSuccessType]);

	useEffect(() => {
		push({
			title: 'EmailPasswordSignInFlow',
			style: Style.Light,
		});

		return () => {
			pop();
		};
	}, [push, pop]);

	useEffect(() => {
		if (!isVerificationSuccessNotSameDevice && !isVerificationFailure) {
			setBackInterceptor(handleBottomSliderBackClick);
		}

		return () => {
			setBackInterceptor(null);
		};
	}, [
		isVerificationFailure,
		handleBottomSliderBackClick,
		setBackInterceptor,
		isVerificationSuccessNotSameDevice,
	]);

	useEffect(() => {
		if (isVerificationSuccessNotSameDevice || isVerificationFailure) {
			setCloseInterceptor(onBottomSliderClose);
		}

		return () => {
			setCloseInterceptor(null);
		};
	}, [
		isVerificationFailure,
		onBottomSliderClose,
		setCloseInterceptor,
		isVerificationSuccessNotSameDevice,
	]);

	const onEmailPasswordLogin: SubmitHandler<IFormInput> = async (data) => {
		const handleEmailPasswordSignIn = async () => {
			const { email, password } = data;

			pushAuthEvent('login_attempt', 'email');

			const { user: firebaseUser } = await signInWithEmailAndPassword(
				auth,
				email,
				password
			);

			return firebaseUser;
		};

		return processLogin(handleEmailPasswordSignIn);
	};

	const processLogin = async (signInFn: () => Promise<User>) => {
		try {
			dispatch({ type: 'SET_IS_LOADING', payload: true });

			const firebaseUser = await signInFn();
			const userData = await api.get('/api/whoami');
			await linkPendingCredential();
			navigate(location.pathname, { replace: true });

			if (
				userData.pleaseComplete &&
				verificationStatus !== VerificationStatus.rejectNoUserData &&
				firebaseUser?.emailVerified
			) {
				return navigate(
					{
						pathname: '/welcome',
						search: 'personalDetails',
					},
					{
						state: {
							title: loginTitleComp ? 'notSameDevice' : 'sameDeviceTitle1',
						},
					}
				);
			}

			if (verificationStatus === VerificationStatus.rejectNoUserData) {
				dispatch({
					type: 'SET_SUCCESS_TYPE',
					payload: SuccessStep.registrationComplete,
				});
				navigate({
					pathname: '/welcome',
					search: 'registration',
				});

				reinitCountdownGuard();

				return api.post('/api/command/verifyEmail', {
					app: 'PASSENGER',
				});
			} else if (firebaseUser?.emailVerified && !userData.pleaseComplete) {
				personalDetailsManager.set('completed');
				localStorage.removeItem('emailForSignIn');

				pushAuthEvent('login', 'email');

				return !Capacitor.isNativePlatform()
					? navigate({ pathname: '/welcome', search: 'tryOurApp' })
					: navigate('/home');
			} else {
				pushAuthEvent('login', 'email');

				return navigate({
					pathname: '/welcome',
					search: 'verifyBeforeStart',
				});
			}
		} catch (err) {
			Sentry.addBreadcrumb({
				category: 'auth',
				message: 'Passenger login flow',
			});
			Sentry.captureException(err);
			dispatch({ type: 'SET_AUTH_ERROR', payload: true });
		} finally {
			dispatch({ type: 'SET_IS_LOADING', payload: false });
		}
	};

	return (
		<LoginScreen
			testId={
				[
					VerificationStatus.successNotSameDeviceApp,
					VerificationStatus.successNotSameDeviceWeb,
				].includes(verificationStatus as VerificationStatus)
					? 'successfull-account-verification-content'
					: 'default-login-content'
			}
			onPasswordless={onPasswordlessLogin}
			onEmailPasswordLogin={onEmailPasswordLogin}
			authError={isAuthError}
			titleComp={loginTitleComp}
			successStep={successType}
			changeSuccessType={setSuccessType}
			render={({ content, submit }) => (
				<MainWithFooterTemplate
					className="mainWithFooter"
					content={
						<ContentWrapper>
							{!shouldHideLogo && (
								<MoplaLogoImgStyled
									className="logo"
									src={moplaWelcomeLogo}
									alt=""
								/>
							)}
							{content}
						</ContentWrapper>
					}
					footer={<Footer>{submit}</Footer>}
				/>
			)}
		/>
	);
};
